import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Pencil, Check, Eye, EyeOff, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api, errMsg, imgUrl } from "@/lib/api";
import { Card } from "./BookingsTab";
import { Field } from "./ListingTab";

const KEYS = [["cleanliness", "Limpieza"], ["accuracy", "Veracidad"], ["checkin", "Llegada"], ["communication", "Comunicación"], ["location", "Ubicación"], ["value", "Calidad-precio"]];
const ST = { published: ["Publicada", "bg-[#264639]/15 text-[#264639]"], pending: ["Pendiente", "bg-[#C4823F]/15 text-[#7a4a1c]"], hidden: ["Oculta", "bg-[#2C2016]/10 text-[#2C2016]/60"] };

const Row = ({ r, onChange, onDelete }) => {
  const [edit, setEdit] = useState(false);
  const [d, setD] = useState(r);
  const save = async (patch) => {
    try {
      const { data } = await api.put(`/admin/reviews/${r.id}`, patch);
      onChange(data);
      setEdit(false);
      toast.success("Reseña actualizada");
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Card className="space-y-3" data-testid={`admin-review-${r.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="font-semibold">{r.author} <span className="text-xs font-normal text-[#4A3525]">· {r.date} · {"★".repeat(r.rating || 5)} · {r.source === "airbnb" ? "Airbnb" : "Huésped web"}</span></p>
        <div className="flex items-center gap-1">
          <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${ST[r.status]?.[1]}`}>{ST[r.status]?.[0]}</span>
          {r.status !== "published" && <button title="Publicar" data-testid={`review-publish-${r.id}`} onClick={() => save({ status: "published" })} className="rounded p-1.5 text-[#264639] hover:bg-[#264639]/10"><Eye className="h-4 w-4" /></button>}
          {r.status === "published" && <button title="Ocultar" data-testid={`review-hide-${r.id}`} onClick={() => save({ status: "hidden" })} className="rounded p-1.5 hover:bg-[#2C2016]/5"><EyeOff className="h-4 w-4" /></button>}
          <button title="Editar" data-testid={`review-edit-${r.id}`} onClick={() => { setD(r); setEdit(!edit); }} className="rounded p-1.5 hover:bg-[#2C2016]/5">{edit ? <X className="h-4 w-4" /> : <Pencil className="h-4 w-4" />}</button>
          <button title="Eliminar" data-testid={`review-delete-${r.id}`} onClick={() => window.confirm("¿Eliminar esta reseña?") && onDelete(r.id)} className="rounded p-1.5 text-[#B8532F] hover:bg-[#B8532F]/10"><Trash2 className="h-4 w-4" /></button>
        </div>
      </div>
      {edit ? (
        <div className="space-y-2">
          <div className="grid gap-2 sm:grid-cols-3">
            <Input value={d.author} onChange={(e) => setD({ ...d, author: e.target.value })} />
            <Input value={d.date || ""} onChange={(e) => setD({ ...d, date: e.target.value })} />
            <Input type="number" min="1" max="5" value={d.rating} onChange={(e) => setD({ ...d, rating: Number(e.target.value) })} />
          </div>
          <Textarea className="min-h-[100px]" value={d.text} onChange={(e) => setD({ ...d, text: e.target.value })} />
          {d.media?.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {d.media.map((m, i) => (
                <div key={i} className="relative h-16 w-16 overflow-hidden rounded-lg">
                  {m.type === "video" ? <video src={imgUrl(m.url)} className="h-full w-full object-cover" /> : <img src={imgUrl(m.url)} alt="" className="h-full w-full object-cover" />}
                  <button onClick={() => setD({ ...d, media: d.media.filter((_, j) => j !== i) })} className="absolute right-0.5 top-0.5 rounded-full bg-black/60 p-0.5 text-white"><X className="h-3 w-3" /></button>
                </div>
              ))}
            </div>
          )}
          <button data-testid={`review-save-${r.id}`} onClick={() => save({ author: d.author, date: d.date, rating: d.rating, text: d.text, media: d.media })} className="flex items-center gap-1 rounded-lg bg-[#2C2016] px-4 py-2 text-sm text-white"><Check className="h-4 w-4" />Guardar</button>
        </div>
      ) : (
        <>
          <p className="line-clamp-4 whitespace-pre-line text-sm text-[#3b2c1f]">{r.text}</p>
          {r.media?.length > 0 && <div className="flex gap-2">{r.media.map((m, i) => <a key={i} href={imgUrl(m.url)} target="_blank" rel="noreferrer" className="text-xs underline">{m.type === "video" ? "Video" : "Foto"} {i + 1}</a>)}</div>}
          {r.email && <p className="text-xs text-[#4A3525]">{r.email}</p>}
        </>
      )}
    </Card>
  );
};

export const ReviewsTab = ({ prop, onSaved }) => {
  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0);
  const [pending, setPending] = useState(0);
  const [filter, setFilter] = useState("");
  const [sr, setSr] = useState(() => ({ ...(prop.sub_ratings || {}) }));
  const load = useCallback(async (skip = 0) => {
    const q = filter === "guest" ? "&source=guest" : filter ? `&status=${filter}` : "";
    const { data } = await api.get(`/admin/reviews?property_id=${prop.id}&skip=${skip}&limit=30${q}`);
    setList((cur) => (skip ? [...cur, ...(data.items || [])] : data.items || []));
    setTotal(data.total || 0);
    setPending(data.pending || 0);
  }, [prop.id, filter]);
  useEffect(() => { load(0); }, [load]);
  const del = async (id) => {
    try { await api.delete(`/admin/reviews/${id}`); setList((l) => l.filter((x) => x.id !== id)); setTotal((n) => n - 1); toast.success("Reseña eliminada"); } catch (e) { toast.error(errMsg(e)); }
  };
  const saveSr = async () => {
    try {
      const sub = Object.fromEntries(Object.entries(sr).map(([k, v]) => [k, Number(v) || 0]));
      const { data } = await api.put(`/admin/properties/${prop.id}`, { sub_ratings: sub });
      onSaved(data);
      toast.success("Calificaciones guardadas");
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="mt-6 grid gap-6 lg:grid-cols-12" data-testid="admin-reviews">
      <div className="space-y-3 lg:col-span-8">
        <div className="flex flex-wrap gap-2">
          {[["", "Todas"], ["pending", `Pendientes (${pending})`], ["published", "Publicadas"], ["hidden", "Ocultas"], ["guest", "De la web"]].map(([v, l]) => (
            <button key={v || "all"} data-testid={`reviews-filter-${v || "all"}`} onClick={() => setFilter(v)} className={`rounded-full px-4 py-1.5 text-sm ${filter === v ? "bg-[#2C2016] text-[#FBF8F2]" : "bg-white"}`}>{l}</button>
          ))}
        </div>
        <p className="text-sm text-[#4A3525]">{total} reseñas</p>
        {list.map((r) => <Row key={r.id} r={r} onDelete={del} onChange={(n) => setList((l) => l.map((x) => (x.id === n.id ? n : x)))} />)}
        {list.length < total && <button onClick={() => load(list.length)} className="w-full rounded-xl border border-[#2C2016]/20 bg-white py-3 text-sm font-semibold">Cargar más</button>}
      </div>
      <div className="lg:col-span-4">
        <Card className="space-y-3 lg:sticky lg:top-24">
          <p className="font-display text-2xl">Calificaciones por categoría</p>
          <div className="grid grid-cols-2 gap-3">
            {KEYS.map(([k, l]) => <Field key={k} label={l}><Input type="number" step="0.01" min="0" max="5" value={sr[k] ?? ""} onChange={(e) => setSr({ ...sr, [k]: e.target.value })} /></Field>)}
          </div>
          <button data-testid="subratings-save-button" onClick={saveSr} className="w-full rounded-lg bg-[#B8532F] py-2.5 text-sm font-semibold text-white">Guardar calificaciones</button>
        </Card>
      </div>
    </div>
  );
};
